// # Notebook Details Modal Component
import React, { useState } from 'react';
import { FaTimes, FaPlay, FaImage, FaInfoCircle, FaDownload } from 'react-icons/fa';
import ankiImage from '../../../aneo_media/community/take_notes/anki.png';
import obsidianImage from '../../../aneo_media/community/take_notes/obsidian.png';
import onenoteImage from '../../../aneo_media/community/take_notes/onenote.png';
import logseqImage from '../../../aneo_media/community/take_notes/logseq.png';
import './notebooks.css';

export const NotebookModal = ({ isOpen, onClose, notebook, isDarkMode }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!isOpen) return null;

  // # Render different content based on notebook type and active tab
  const renderContent = () => {
    switch (notebook) {
      case 'anki':
        return (
          <>
            <h2>Anki Flashcards</h2>
            
            {/* # Navigation Tabs */}
            <div className="ext_nb_tabs">
              <button 
                className={`ext_nb_tab ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveTab('overview')}
              >
                <FaInfoCircle /> Overview
              </button>
              <button 
                className={`ext_nb_tab ${activeTab === 'tutorial' ? 'active' : ''}`}
                onClick={() => setActiveTab('tutorial')}
              >
                <FaPlay /> Tutorial
              </button>
              <button 
                className={`ext_nb_tab ${activeTab === 'preview' ? 'active' : ''}`}
                onClick={() => setActiveTab('preview')}
              >
                <FaImage /> Preview
              </button>
              <button 
                className={`ext_nb_tab ${activeTab === 'download' ? 'active' : ''}`}
                onClick={() => setActiveTab('download')}
              >
                <FaDownload /> Download
              </button>
            </div>

            {/* # Tab Content */}
            <div className="ext_nb_tab-content">
              {activeTab === 'overview' && (
                <div className="ext_nb_overview">
                  <p className="ext_nb_description">
                    The best option for old gunners, who like the idea of "Spaced Repetition".
                    You will need to learn how to make flashcards, but don't worry; you don't need any skills to use premade cards.
                  </p>
                  <div className="ext_nb_features">
                    <h3>Key Features</h3>
                    <ul>
                      <li>Spaced repetition algorithm</li>
                      <li>Customizable card templates</li>
                      <li>Support for images and audio</li>
                      <li>Progress tracking and statistics</li>
                      <li>Large community and shared decks</li>
                    </ul>
                  </div>
                </div>
              )}

              {activeTab === 'tutorial' && (
                <div className="ext_nb_video-container">
                  <iframe
                    width="100%"
                    height="500"
                    src="https://www.youtube.com/embed/AjdUxCnAXJw"
                    title="Anki Tutorial"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              )}

              {activeTab === 'preview' && (
                <div className="ext_nb_preview">
                  <div className="ext_nb_image-container">
                    <img 
                      src={ankiImage}
                      alt="Anki Interface" 
                      className="ext_nb_preview-image"
                    />
                  </div>
                  <p className="ext_nb_image-caption">
                    Anki's clean and efficient interface for flashcard review
                  </p>
                </div>
              )}

              {activeTab === 'download' && (
                <div className="ext_nb_download">
                  <div className="ext_nb_download-options">
                    <a href="https://apps.ankiweb.net/" target="_blank" rel="noopener noreferrer" className="ext_nb_download-btn">
                      <FaDownload /> Download Anki
                    </a>
                    <p className="ext_nb_download-info">
                      Available for Windows, Mac, Linux, iOS, and Android
                    </p>
                  </div>
                </div>
              )}
            </div>
          </>
        );

      case 'obsidian':
        return (
          <>
            <h2>Obsidian Notebook</h2>
            
            <div className="ext_nb_tabs">
              <button 
                className={`ext_nb_tab ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveTab('overview')}
              >
                <FaInfoCircle /> Overview
              </button>
              <button 
                className={`ext_nb_tab ${activeTab === 'tutorial' ? 'active' : ''}`}
                onClick={() => setActiveTab('tutorial')}
              >
                <FaPlay /> Tutorial
              </button>
              <button 
                className={`ext_nb_tab ${activeTab === 'preview' ? 'active' : ''}`}
                onClick={() => setActiveTab('preview')}
              >
                <FaImage /> Preview
              </button>
              <button 
                className={`ext_nb_tab ${activeTab === 'download' ? 'active' : ''}`}
                onClick={() => setActiveTab('download')}
              >
                <FaDownload /> Download
              </button>
            </div>

            <div className="ext_nb_tab-content">
              {activeTab === 'overview' && (
                <div className="ext_nb_overview">
                  <p className="ext_nb_description">
                    A smart notebook that connects ideas & notes swiftly, you probably own a second brain now! 
                    With the "Mind-map" thingy, you can slide between ideas like a Ferrari.
                  </p>
                  <div className="ext_nb_features">
                    <h3>Key Features</h3>
                    <ul>
                      <li>Offers a whiteboard "Canvas"</li>
                      <li>Powerful linking between notes</li>
                      <li>Graph view for connections</li>
                      <li>Markdown support</li>
                      <li>Extensive plugin system</li>
                    </ul>
                  </div>
                </div>
              )}

              {activeTab === 'tutorial' && (
                <div className="ext_nb_video-container">
                  <iframe
                    width="100%"
                    height="500"
                    src="https://www.youtube.com/embed/hSTy_BInQs8"
                    title="Obsidian Tutorial"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              )}

              {activeTab === 'preview' && (
                <div className="ext_nb_preview">
                  <div className="ext_nb_image-container">
                    <img 
                      src={obsidianImage}
                      alt="Obsidian Interface" 
                      className="ext_nb_preview-image"
                    />
                  </div>
                  <p className="ext_nb_image-caption">
                    Obsidian's powerful interface for connected note-taking
                  </p>
                </div>
              )}

              {activeTab === 'download' && (
                <div className="ext_nb_download">
                  <div className="ext_nb_download-options">
                    <a href="https://obsidian.md/download" target="_blank" rel="noopener noreferrer" className="ext_nb_download-btn">
                      <FaDownload /> Download Obsidian
                    </a>
                    <p className="ext_nb_download-info">
                      Available for Windows, Mac, Linux, iOS, and Android
                    </p>
                  </div>
                </div>
              )}
            </div>
          </>
        );

      case 'onenote':
        return (
          <>
            <h2>OneNote Notebook</h2>
            
            <div className="ext_nb_tabs">
              <button 
                className={`ext_nb_tab ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveTab('overview')}
              >
                <FaInfoCircle /> Overview
              </button>
              <button 
                className={`ext_nb_tab ${activeTab === 'tutorial' ? 'active' : ''}`}
                onClick={() => setActiveTab('tutorial')}
              >
                <FaPlay /> Tutorial
              </button>
              <button 
                className={`ext_nb_tab ${activeTab === 'preview' ? 'active' : ''}`}
                onClick={() => setActiveTab('preview')}
              >
                <FaImage /> Preview
              </button>
              <button 
                className={`ext_nb_tab ${activeTab === 'download' ? 'active' : ''}`}
                onClick={() => setActiveTab('download')}
              >
                <FaDownload /> Download
              </button>
            </div>

            <div className="ext_nb_tab-content">
              {activeTab === 'overview' && (
                <div className="ext_nb_overview">
                  <p className="ext_nb_description">
                    A whiteboard that works Just like Microsoft Word, with much more space, freedom and flexibility. 
                    Just basic, for people who like keeping it simple.
                  </p>
                  <div className="ext_nb_features">
                    <h3>Key Features</h3>
                    <ul>
                      <li>Familiar Microsoft Office interface</li>
                      <li>Free-form canvas</li>
                      <li>Easy organization with notebooks and sections</li>
                      <li>Cross-platform synchronization</li>
                      <li>Integration with other Microsoft tools</li>
                    </ul>
                  </div>
                </div>
              )}

              {activeTab === 'tutorial' && (
                <div className="ext_nb_video-container">
                  <iframe
                    width="100%"
                    height="500"
                    src="https://www.youtube.com/embed/7zsPvikQP7I"
                    title="OneNote Tutorial"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              )}

              {activeTab === 'preview' && (
                <div className="ext_nb_preview">
                  <div className="ext_nb_image-container">
                    <img 
                      src={onenoteImage}
                      alt="OneNote Interface" 
                      className="ext_nb_preview-image"
                    />
                  </div>
                  <p className="ext_nb_image-caption">
                    OneNote's familiar and flexible interface
                  </p>
                </div>
              )}

              {activeTab === 'download' && (
                <div className="ext_nb_download">
                  <div className="ext_nb_download-options">
                    <a href="https://www.microsoft.com/en-us/microsoft-365/onenote/digital-note-taking-app" target="_blank" rel="noopener noreferrer" className="ext_nb_download-btn">
                      <FaDownload /> Download OneNote
                    </a>
                    <p className="ext_nb_download-info">
                      Available for Windows, Mac, iOS, and Android
                    </p>
                  </div>
                </div>
              )}
            </div>
          </>
        );

      case 'logseq':
        return (
          <>
            <h2>Logseq Notebook</h2>
            
            <div className="ext_nb_tabs">
              <button 
                className={`ext_nb_tab ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveTab('overview')}
              >
                <FaInfoCircle /> Overview
              </button>
              <button 
                className={`ext_nb_tab ${activeTab === 'tutorial' ? 'active' : ''}`}
                onClick={() => setActiveTab('tutorial')}
              >
                <FaPlay /> Tutorial
              </button>
              <button 
                className={`ext_nb_tab ${activeTab === 'preview' ? 'active' : ''}`}
                onClick={() => setActiveTab('preview')}
              >
                <FaImage /> Preview
              </button>
              <button 
                className={`ext_nb_tab ${activeTab === 'download' ? 'active' : ''}`}
                onClick={() => setActiveTab('download')}
              >
                <FaDownload /> Download
              </button>
            </div>

            <div className="ext_nb_tab-content">
              {activeTab === 'overview' && (
                <div className="ext_nb_overview">
                  <p className="ext_nb_description">
                    A smart notebook that connects ideas & notes just like obsidian, with the advantage of incorporating 
                    flashcards into your notes, works like magic when you master it.
                  </p>
                  <div className="ext_nb_features">
                    <h3>Key Features</h3>
                    <ul>
                      <li>Offers a whiteboard</li>
                      <li>Built-in flashcard system</li>
                      <li>Powerful linking and organization</li>
                      <li>Open source</li>
                      <li>Local-first storage</li>
                    </ul>
                  </div>
                </div>
              )}

              {activeTab === 'tutorial' && (
                <div className="ext_nb_video-container">
                  <iframe
                    width="100%"
                    height="500"
                    src="https://www.youtube.com/embed/pS5fyFWsZT4"
                    title="Logseq Tutorial"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              )}

              {activeTab === 'preview' && (
                <div className="ext_nb_preview">
                  <div className="ext_nb_image-container">
                    <img 
                      src={logseqImage}
                      alt="Logseq Interface" 
                      className="ext_nb_preview-image"
                    />
                  </div>
                  <p className="ext_nb_image-caption">
                    Logseq's powerful interface combining notes and flashcards
                  </p>
                </div>
              )}

              {activeTab === 'download' && (
                <div className="ext_nb_download">
                  <div className="ext_nb_download-options">
                    <a href="https://logseq.com/downloads" target="_blank" rel="noopener noreferrer" className="ext_nb_download-btn">
                      <FaDownload /> Download Logseq
                    </a>
                    <p className="ext_nb_download-info">
                      Available for Windows, Mac, Linux, iOS, and Android
                    </p>
                  </div>
                </div>
              )}
            </div>
          </>
        );

      case 'upnotes':
        return (
          <>
            <h2>UpNotes Notebook</h2>
            
            <div className="ext_nb_tabs">
              <button 
                className={`ext_nb_tab ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveTab('overview')}
              >
                <FaInfoCircle /> Overview
              </button>
              <button 
                className={`ext_nb_tab ${activeTab === 'download' ? 'active' : ''}`}
                onClick={() => setActiveTab('download')}
              >
                <FaDownload /> Download
              </button>
            </div>

            <div className="ext_nb_tab-content">
              {activeTab === 'overview' && (
                <div className="ext_nb_overview">
                  <p className="ext_nb_description">
                    Also smart notebook that connects ideas & notes just like obsidian, works really well when you learn it.
                  </p>
                  <div className="ext_nb_features">
                    <h3>Coming Soon</h3>
                    <p>More details about UpNotes features will be available soon.</p>
                  </div>
                </div>
              )}

              {activeTab === 'download' && (
                <div className="ext_nb_download">
                  <div className="ext_nb_download-options">
                    <a href="https://upnotes.io/download" target="_blank" rel="noopener noreferrer" className="ext_nb_download-btn">
                      <FaDownload /> Download UpNotes
                    </a>
                    <p className="ext_nb_download-info">
                      Available for Windows, Mac, iOS, and Android
                    </p>
                  </div>
                </div>
              )}
            </div>
          </>
        );

      default:
        return <p>Content coming soon...</p>;
    }
  };

  return (
    <div className={`ext_nb_modal-overlay ${isDarkMode ? 'ext_nb_dark-mode' : ''}`} onClick={onClose}>
      <div className="ext_nb_modal" onClick={e => e.stopPropagation()}>
        <button className="ext_nb_close-btn" onClick={onClose}>
          <FaTimes />
        </button>
        <div className="ext_nb_modal-content">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default NotebookModal;
